import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { format, isToday, isYesterday } from "date-fns";
import { motion } from "framer-motion";

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [minMontant, setMinMontant] = useState("");
  const [maxMontant, setMaxMontant] = useState("");
  const [categorieFilter, setCategorieFilter] = useState("");

  const categories = useMemo(() => {
    const set = new Set();

    logs.forEach((log) => {
      try {
        const d =
          typeof log.details === "string"
            ? JSON.parse(log.details)
            : log.details;

        if (d?.categorie) set.add(d.categorie);
        if (d?.avant?.categorie) set.add(d.avant.categorie);
        if (d?.apres?.categorie) set.add(d.apres.categorie);
      } catch {}
    });

    return Array.from(set);
  }, [logs]);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await api.get("/depenses/audit_logs");
      setLogs(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getAction = (log) =>
    log.action || log.actions || "UNKNOWN";

  // 🔥 FORMAT MESSAGE
  const formatAuditMessage = (log, details) => {
    const user = log.nom || "Utilisateur";

    // =========================
    // DELETE
    // =========================
    if (log.action === "DELETE") {
      return (
        `🗑️ ${user} a supprimé la dépense ` +
        `"${details?.description || "Sans description"}" ` +
        `d'un montant de ${details?.montant || "?"}.`
      );
    }

    // =========================
    // UPDATE
    // =========================
    if (
      log.action === "UPDATE" &&
      details?.avant &&
      details?.apres
    ) {
      const avant = details.avant;
      const apres = details.apres;

      const changements = [];

      if (
        String(avant.montant ?? "") !==
        String(apres.montant ?? "")
      ) {
        changements.push(
          `💰 Montant : ${avant.montant} → ${apres.montant}`
        );
      }

      if (
        String(avant.categorie ?? "") !==
        String(apres.categorie ?? "")
      ) {
        changements.push(
          `📂 Catégorie : ${avant.categorie || "-"} → ${apres.categorie || "-"}`
        );
      }

      if (
        String(avant.description ?? "") !==
        String(apres.description ?? "")
      ) {
        changements.push(
          `📝 Description : "${avant.description || "-"}" → "${apres.description || "-"}"`
        );
      }

      if (
        String(avant.mode_paiement ?? "") !==
        String(apres.mode_paiement ?? "")
      ) {
        changements.push(
          `💳 Paiement : ${avant.mode_paiement || "-"} → ${apres.mode_paiement || "-"}`
        );
      }

      if (
        String(avant.personnel_id ?? "") !==
        String(apres.personnel_id ?? "")
      ) {
        changements.push(
          `👤 Personnel : ${avant.personnel_id || "-"} → ${apres.personnel_id || "-"}`
        );
      }

      if (
        String(avant.periode_salaire ?? "") !==
        String(apres.periode_salaire ?? "")
      ) {
        changements.push(
          `📅 Période : ${avant.periode_salaire || "-"} → ${apres.periode_salaire || "-"}`
        );
      }

      if (!changements.length) {
        return `✏️ ${user} a enregistré une modification sans changement détecté.`;
      }

      return `✏️ ${user} a modifié la dépense : ${changements.join(" | ")}`;
    }

    return `${user} a effectué une action`;
  };

  // 🔥 GROUPEMENT PAR DATE
  const groupedLogs = useMemo(() => {
    const groups = {};

    logs.forEach((log) => {
      const date = new Date(log.created_at);

      let label = format(date, "dd/MM/yyyy");
      if (isToday(date)) label = "Aujourd’hui";
      else if (isYesterday(date)) label = "Hier";

      if (!groups[label]) groups[label] = [];
      groups[label].push(log);
    });

    return groups;
  }, [logs]);

  // 🔥 FILTRE
  const filteredGroups = Object.entries(groupedLogs).map(([date, items]) => {

    const filtered = items.filter((log) => {
      const action = getAction(log);

      if (filter !== "all" && action !== filter) return false;

      let details = {};
      try {
        details =
          typeof log.details === "string"
            ? JSON.parse(log.details)
            : log.details || {};
      } catch {}

      const texte = JSON.stringify(details).toLowerCase();

      // 🔎 RECHERCHE TEXTE
      if (search && !texte.includes(search.toLowerCase()) && !(log.nom || "").toLowerCase().includes(search.toLowerCase())) {
        return false;
      }

      // 💰 MONTANT
      const montant =
        details?.montant ||
        details?.apres?.montant ||
        details?.avant?.montant;

      if (minMontant && montant < Number(minMontant)) return false;
      if (maxMontant && montant > Number(maxMontant)) return false;

      // 📂 CATEGORIE
      const categorie =
        details?.categorie ||
        details?.apres?.categorie ||
        details?.avant?.categorie;

      if (categorieFilter && categorie !== categorieFilter) return false;

      return true;
    });

    return { date, items: filtered };

  }).filter(group => group.items.length > 0);

  return (
    <div className="p-6 bg-gray-100 min-h-screen space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Historique des dépenses</h2>

        <button
          onClick={() => navigate("/depenses")}
          className="bg-gray-800 text-white px-4 py-2 rounded"
        >
          ← Retour
        </button>
      </div>

      <div className="flex flex-wrap gap-3">

        {/* RECHERCHE */}
        <input
          type="text"
          placeholder="🔎 Rechercher (ex: taxi)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border p-2 rounded"
        />

        {/* MONTANT MIN */}
        <input
          type="number"
          placeholder="Montant min"
          value={minMontant}
          onChange={(e) => setMinMontant(e.target.value)}
          className="border p-2 rounded w-32"
        />

        {/* MONTANT MAX */}
        <input
          type="number"
          placeholder="Montant max"
          value={maxMontant}
          onChange={(e) => setMaxMontant(e.target.value)}
          className="border p-2 rounded w-32"
        />

        {/* CATEGORIE */}
        <select
          value={categorieFilter}
          onChange={(e) => setCategorieFilter(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="">Toutes catégories</option>
          {categories.map((c, i) => (
            <option key={i} value={c}>{c}</option>
          ))}
        </select>

      </div>

      {/* FILTRE */}
      <select
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="border p-2 rounded"
      >
        <option value="all">Toutes actions</option>
        <option value="UPDATE">Modifications</option>
        <option value="DELETE">Suppressions</option>
      </select>

      {/* TIMELINE */}
      <div className="space-y-6">

        {filteredGroups.length === 0 && (
          <p className="text-center text-gray-500 py-10">
            Aucun historique trouvé
          </p>
        )}

        {filteredGroups.map((group, i) => (
          <div key={i}>

            {/* DATE */}
            <div className="text-center text-sm text-gray-500 mb-4 font-semibold">
              {group.date}
            </div>

            {/* ITEMS */}
            <div className="space-y-4">
              {group.items.map((log, index) => {
                let details = {};
                try {
                  details =
                    typeof log.details === "string"
                      ? JSON.parse(log.details)
                      : log.details || {};
                } catch {}

                const action = getAction(log);
                const isDelete = action === "DELETE";

                return (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: isDelete ? -50 : 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex ${isDelete ? "justify-start" : "justify-end"}`}
                  >
                    <div className="flex items-end gap-2 max-w-md">

                      {/* AVATAR */}
                      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold">
                        {(log.nom || "?")[0]}
                      </div>

                      {/* BULLE */}
                      <div
                        className={`px-4 py-3 rounded-2xl shadow text-sm ${
                          isDelete
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-900"
                        }`}
                      >
                        <p>{formatAuditMessage(log, details)}</p>

                        <div className="text-xs text-gray-500 mt-2 text-right">
                          {format(new Date(log.created_at), "HH:mm")}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      
    </div>
  );
}
